import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSupport } from "../context/SupportContext";
import { useGeoLocation } from "../../Billing/hooks/useGeoLocation";
import { Card } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Badge } from "@shadcn-in-nx/ui/badge";
import {
    MessageCircle,
    ExternalLink,
    Headphones,
    Clock,
    Video,
    CheckCircle,
    ArrowRight,
    Sparkles,
} from "lucide-react";

const LATAM_HOURLY_RATE = 75;

export default function SupportAccessView() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isLatam } = useGeoLocation();
    const {
        supportAccess,
        supportPlans,
    } = useSupport();

    const handlePurchasePlan = () => {
        navigate("/billing#support");
    };

    return (
        <div className="min-h-screen p-4 md:p-8">
            <div className="max-w-5xl mx-auto space-y-8">
                {/* Header */}
                <div className="text-center space-y-4">
                    <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                        Support Center
                    </h1>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                        {supportAccess?.message || "Your current plan does not include direct support."}
                    </p>
                </div>

                {/* Community Support Card */}
                <Card className="p-6 md:p-8 border-2 border-border/50">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                        <div className="w-16 h-16 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <MessageCircle className="w-8 h-8 text-indigo-600" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-semibold mb-2">Community Support</h2>
                            <p className="text-muted-foreground mb-4">
                                Join our Discord community to get help from other users and our team.
                                Share ideas, ask questions, and connect with fellow builders.
                            </p>
                            {supportAccess?.discordUrl && (
                                <Button
                                    variant="outline"
                                    asChild
                                    className="gap-2"
                                >
                                    <a
                                        href={supportAccess.discordUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Join Discord
                                        <ExternalLink className="w-4 h-4" />
                                    </a>
                                </Button>
                            )}
                        </div>
                    </div>
                </Card>

                {/* Upgrade CTA */}
                {supportAccess?.upgradeUrl && (
                    <Card className="p-6 md:p-8 bg-gradient-to-br from-gray-900 to-gray-800 text-white border-0">
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                            <div className="w-16 h-16 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                                <Sparkles className="w-8 h-8 text-white" />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-xl font-semibold mb-2">Upgrade Your Plan</h2>
                                <p className="text-gray-300 mb-4">
                                    Upgrade to a higher plan to unlock priority email support and access
                                    to our ticketing system for faster response times.
                                </p>
                                <Button
                                    asChild
                                    className="bg-white text-gray-900 hover:bg-gray-100 gap-2"
                                >
                                    <a href={supportAccess.upgradeUrl}>
                                        View Plans
                                        <ArrowRight className="w-4 h-4" />
                                    </a>
                                </Button>
                            </div>
                        </div>
                    </Card>
                )}

                {/* Hands-On Support Section */}
                {supportAccess?.handsOnSupport && (
                    <div className="space-y-6">
                        <div className="text-center">
                            <Badge variant="secondary" className="mb-4">
                                <Headphones className="w-3 h-3 mr-1" />
                                Premium Support
                            </Badge>
                            <h2 className="text-2xl md:text-3xl font-bold mb-3">
                                {supportAccess.handsOnSupport.title || "Hands-On Support"}
                            </h2>
                            <p className="text-muted-foreground max-w-2xl mx-auto">
                                {supportAccess.handsOnSupport.description ||
                                    "Get dedicated 1:1 time with our team for screen sharing sessions, debugging help, architecture reviews, and custom implementation guidance."}
                            </p>
                            {isLatam && (
                                <p className="text-center text-green-600 text-sm mt-3">
                                    🌎 {t("billing.supportPlans.latamPricing")}
                                </p>
                            )}
                        </div>

                        {/* Support Plans Grid */}
                        <div className="grid md:grid-cols-3 gap-6">
                            {(supportAccess.handsOnSupport.plans || supportPlans).map((plan) => {
                                const latamPrice = plan.hours * LATAM_HOURLY_RATE;
                                const displayPrice = isLatam ? latamPrice : plan.price;
                                const displayHourlyRate = isLatam ? LATAM_HOURLY_RATE : plan.hourlyRate;

                                return (
                                    <Card
                                        key={plan.tier}
                                        className={`p-6 border-2 transition-all hover:shadow-lg ${
                                            plan.tier === "STANDARD_8H"
                                                ? "border-gray-900 relative"
                                                : "border-border/50"
                                        }`}
                                    >
                                        {plan.tier === "STANDARD_8H" && (
                                            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-900 text-white">
                                                Most Popular
                                            </Badge>
                                        )}

                                        <div className="text-center mb-6">
                                            <h3 className="text-lg font-semibold mb-1">{plan.name}</h3>
                                            <p className="text-sm text-muted-foreground mb-4">
                                                {plan.description}
                                            </p>
                                            <div className="mb-2">
                                                {isLatam && (
                                                    <span className="text-lg text-muted-foreground line-through mr-2">${plan.price}</span>
                                                )}
                                                <span className="text-4xl font-bold">${displayPrice}</span>
                                                <span className="text-muted-foreground">/month</span>
                                            </div>
                                            <p className="text-sm text-muted-foreground">
                                                ${displayHourlyRate}/hour
                                            </p>
                                        </div>

                                        <div className="space-y-3 mb-6">
                                            {plan.features?.map((feature, idx) => (
                                                <div key={idx} className="flex items-start gap-2">
                                                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                                    <span className="text-sm">{feature}</span>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-4">
                                            <Clock className="w-4 h-4" />
                                            <span>{plan.hours} hours/month</span>
                                        </div>

                                        <Button
                                            onClick={handlePurchasePlan}
                                            className={`w-full ${
                                                plan.tier === "STANDARD_8H"
                                                    ? "bg-gray-900 hover:bg-gray-800 text-white"
                                                    : ""
                                            }`}
                                            variant={plan.tier === "STANDARD_8H" ? "default" : "outline"}
                                        >
                                            {t("billing.supportPlans.getStarted")}
                                        </Button>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* Benefits List */}
                        <Card className="p-6 md:p-8 bg-muted/50 border-2 border-dashed border-border/50">
                            <h3 className="font-semibold mb-4 text-center">
                                What&apos;s included with Hands-On Support:
                            </h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="flex items-start gap-3">
                                    <Video className="w-5 h-5 text-blue-500 mt-0.5" />
                                    <div>
                                        <p className="font-medium">Screen Sharing Sessions</p>
                                        <p className="text-sm text-muted-foreground">
                                            Real-time collaboration with our engineers
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                                    <div>
                                        <p className="font-medium">Priority Response</p>
                                        <p className="text-sm text-muted-foreground">
                                            Fast response times on all inquiries
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Headphones className="w-5 h-5 text-purple-500 mt-0.5" />
                                    <div>
                                        <p className="font-medium">Dedicated Support</p>
                                        <p className="text-sm text-muted-foreground">
                                            Work directly with our team
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Clock className="w-5 h-5 text-orange-500 mt-0.5" />
                                    <div>
                                        <p className="font-medium">Flexible Scheduling</p>
                                        <p className="text-sm text-muted-foreground">
                                            Book sessions at your convenience
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                )}
            </div>
        </div>
    );
}
